import { Checkbox, HTMLTable, Spinner } from "@blueprintjs/core";
import { showSuccess } from "nsitools-react";
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { NotificationPreferenceDto } from "../../api/NotificationPreferenceApi";
import { useCommonHooks } from "../../hooks";
import { ETLCodes } from "../../locales";

export const NotificationsTab: React.FunctionComponent = () => {
  const { t, apis } = useCommonHooks();
  const queryClient = useQueryClient();

  const { data: preferences, isFetching } = useQuery(
    ["notification-preferences"],
    async () => await apis.notificationPreferences.getAll()
  );

  const mutation = useMutation(
    async (dto: NotificationPreferenceDto) =>
      await apis.notificationPreferences.save(dto),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notification-preferences"]);
      },
    }
  );

  const handleToggle = React.useCallback(
    (
      pref: NotificationPreferenceDto,
      field: "emailEnabled" | "pushEnabled"
    ) => {
      const enabled = !pref[field];
      mutation.mutate(
        {
          ...pref,
          [field]: enabled,
        },
        {
          onSuccess: () =>
            showSuccess(
              t(
                enabled
                  ? ETLCodes.NotificationActivee
                  : ETLCodes.NotificationDesactivee,
                {
                  libelle: pref.notificationTypeLibelle,
                  type:
                    field === "emailEnabled"
                      ? t(ETLCodes.Email)
                      : t(ETLCodes.Push),
                }
              )
            ),
        }
      );
    },
    [mutation, t]
  );

  if (isFetching && !preferences) {
    return <Spinner />;
  }

  return (
    <HTMLTable striped style={{ width: "100%" }}>
      <thead>
        <tr>
          <th>{t(ETLCodes.Notifications)}</th>
          <th style={{ textAlign: "center", width: 100 }}>
            {t(ETLCodes.Email)}
          </th>
          <th style={{ textAlign: "center", width: 100 }}>
            {t(ETLCodes.Push)}
          </th>
        </tr>
      </thead>
      <tbody>
        {preferences?.map((pref) => (
          <tr key={pref.idnotificationType}>
            <td>{pref.notificationTypeLibelle}</td>
            <td style={{ textAlign: "center" }}>
              <Checkbox
                checked={pref.emailEnabled}
                onChange={() => handleToggle(pref, "emailEnabled")}
                disabled={pref.emailDisabled}
              />
            </td>
            <td style={{ textAlign: "center" }}>
              <Checkbox
                checked={pref.pushEnabled}
                onChange={() => handleToggle(pref, "pushEnabled")}
                disabled
                style={{ marginBottom: 0 }}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </HTMLTable>
  );
};

export default NotificationsTab;
